import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output, PLATFORM_ID, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { DateUtils } from '../../../../shared/helpers/dateUtils';
import { EYesNo } from '../../../../shared/models/types.enum';
import { last } from 'rxjs';
import { IAssessScreen } from '../../../../shared/models/assessScreen';
import { UserDataService } from '../../../../shared/services/auth/userData.service';
import { TipService } from '../../../../shared/services/tip.service';
import { Icons } from '../../../../shared/helpers/icons';
import { TipModalComponent } from '../../../components/tip-modal/tip-modal.component';
import { ContactsService } from '../../../../shared/services/contact.service';
import { ITip, ITipsResults } from '../../../../shared/models/tipsResults';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
declare var initFlowbite: any;

@Component({
  selector: 'assessment-situation',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    ReactiveFormsModule,
    TipModalComponent,
    SpinnerComponent
  ],
  templateUrl: './assessment-situation.component.html',
  styleUrl: './assessment-situation.component.css'
})
export class AssessmentSituationComponent implements OnInit, AfterViewInit {
  icons = Icons;
  showTipsTable = false;
  canEdit = false;
  canAdmin = false;
  selectedTipId: number | null = null;

  applyTipSummary(summary: string, tipId?: number): void {
    this.situationForm.get('detail_of_situation')?.setValue(summary);
    if (tipId) {
      this.selectedTipId = tipId;
    }
    this.showTipsTable = false;
  }

  yesNo = EYesNo;
  @ViewChild('datepickerIncidentDate') datepickerIncidentDate!: ElementRef;
  situationForm: FormGroup = new FormGroup([]);

  @Input() assessScreenData: any;
  @Output() onSaveSection1 = new EventEmitter<IAssessScreen>();

  loading: boolean = false;
  tips: ITip[] = [];
  tipsError: string | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private userDataService: UserDataService,
    private tipService: TipService,
    private contactsService: ContactsService
  ) {}

  // Fetch tips for the current building with a status of 1 (In Review)
  getTipsByBuildingId(): void {
    this.loading = true;
    this.tips = [];
    this.tipsError = null;
    const buildingId = this.assessScreenData?.building_id;
    if (!buildingId) {
      this.tipsError = 'No building ID available.';
      this.loading = false;
      return;
    }
    this.tipService.getTipsByBuildingId(buildingId).subscribe({
      next: (res: ITipsResults) => {
        if (res && res.payload) {
          this.tips = res.payload.filter((tip: ITip) => tip.status === 1);
          this.loading = false;
        } else {
          this.tips = [];
          this.loading = false;
        }
      },
      error: (err) => {
        this.tipsError = 'Failed to fetch tips.';
        this.loading = false;
      }
    });
  }

  ngOnInit() {
    this.initializeForm();
    const buildingId = this.assessScreenData?.building_id;
    const caseId = this.assessScreenData?.case_id;
    if (buildingId) {
      this.contactsService.getUserPermissions(String(buildingId)).subscribe((perms: any) => {
        this.canEdit = !!perms.permissions?.canEdit;
        this.canAdmin = !!perms.permissions?.canAdmin;
        setTimeout(() => initFlowbite(), 0);
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['assessScreenData'] && this.assessScreenData) {
      this.initializeForm();
    }
  }

  private initializeForm(): void {
    this.situationForm = new FormGroup({
      detail_of_situation: new FormControl(this.assessScreenData?.detail_of_situation || null, Validators.required),
      // set yyyy-mm-dd format by default to save to db
      date_of_incident: new FormControl(this.assessScreenData?.date_of_incident || DateUtils.formatDateForBackend(DateUtils.getCurrentFormattedDate()), Validators.required), 
      time_of_incident: new FormControl(this.assessScreenData?.time_of_incident ||'11:00', Validators.required),
      incident_on_campus: new FormControl(String(this.assessScreenData?.incident_on_campus) || this.yesNo.Yes, Validators.required),
      incident_location: new FormControl(this.assessScreenData?.incident_location || null, Validators.required),
    });
  }

  async ngAfterViewInit(): Promise<void> {
    initFlowbite();
    if (isPlatformBrowser(this.platformId)) {
      // Dynamically import the Datepicker to avoid server-side loading
      const { Datepicker } = await import('flowbite-datepicker');
      if (this.datepickerIncidentDate) {
        new Datepicker(this.datepickerIncidentDate.nativeElement, {
          autohide: true,
          format: 'mm/dd/yyyy'
        });
        // set default value to display for incident date
        if (this.assessScreenData?.date_of_incident) {
          // if date_of_incident is available, set it to the datepicker
          this.datepickerIncidentDate.nativeElement.value = DateUtils.formatDateForDisplay(this.assessScreenData.date_of_incident);
        } else {
          // if date_of_incident is not available, set it to the current date
          this.datepickerIncidentDate.nativeElement.value = DateUtils.getCurrentFormattedDate();
        }
        this.datepickerIncidentDate.nativeElement.addEventListener('changeDate', (event: any) => {
          const formattedDate = DateUtils.formatDateForBackend(event.target.value);
          this.situationForm.get('date_of_incident')?.setValue(formattedDate);
        });
      }
    }
  }

  saveSituation(): void {
    if (this.situationForm.valid) {
      this.onSaveSection1.emit(this.situationForm.value);
      const caseId = this.assessScreenData?.case_id;
      const buildingId = this.assessScreenData?.building_id;
      if (this.selectedTipId && caseId && buildingId) {
        this.tipService.saveTipsAssessRel(buildingId, this.selectedTipId, caseId).subscribe();
      }
    }
  }
}
